import { BoxRect, computeContainRect } from "./containRect";
import { DEFAULT_CROP, PlayCrop } from "../../render/crop";
import { tagSelectedShape, tagShapeAtRect } from "./chlkShape";

export interface InsertImagePayload {
  png: string;
  width: number;
  height: number;
  playId?: string;
}

export interface MultiInsertResult {
  inserted: number;
  requested: number;
  boxes: number;
  failed: number;
}

interface TargetBox {
  rect: BoxRect;
  placeholderId: string | null;
}

// Built lazily: the PowerPoint global does not exist at module load on Mac
// (office.js defines it asynchronously), and touching it here kills the
// whole bundle before the app can render. See startup.imports.test.ts.
let imagePlaceholderTypes: Set<string> | null = null;
function getImagePlaceholderTypes(): Set<string> {
  if (!imagePlaceholderTypes) {
    imagePlaceholderTypes = new Set<string>([
      PowerPoint.PlaceholderType.picture,
      PowerPoint.PlaceholderType.content,
      PowerPoint.PlaceholderType.onlinePicture,
    ]);
  }
  return imagePlaceholderTypes;
}

function assertOfficeReady(): void {
  if (
    typeof Office === "undefined" ||
    !Office.context ||
    typeof PowerPoint === "undefined"
  ) {
    throw new Error("PowerPoint is still starting up — please try again in a moment.");
  }
}

function shapeToRect(shape: PowerPoint.Shape): BoxRect {
  return {
    left: shape.left,
    top: shape.top,
    width: shape.width,
    height: shape.height,
  };
}

function isEmptyImagePlaceholder(shape: PowerPoint.Shape): boolean {
  if (shape.type !== PowerPoint.ShapeType.placeholder) {
    return false;
  }
  const pf = shape.placeholderFormat;
  return getImagePlaceholderTypes().has(pf.type) && pf.containedType === null;
}

function insertViaOfficeLegacy(base64: string, width = 540): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(
      base64,
      { coercionType: Office.CoercionType.Image, imageWidth: width },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Office error ${result.error.code}: ${result.error.message}`));
        } else {
          resolve();
        }
      }
    );
  });
}

function insertAtCoordinates(base64: string, rect: BoxRect): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(
      base64,
      {
        coercionType: Office.CoercionType.Image,
        imageLeft: rect.left,
        imageTop: rect.top,
        imageWidth: rect.width,
        imageHeight: rect.height,
      },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Office error ${result.error.code}: ${result.error.message}`));
        } else {
          resolve();
        }
      }
    );
  });
}

async function collectSelectedBoxes(): Promise<TargetBox[] | null> {
  if (!Office.context.requirements.isSetSupported("PowerPointApi", "1.5")) {
    return null;
  }

  return PowerPoint.run(async (context) => {
    const selectedShapes = context.presentation.getSelectedShapes();
    selectedShapes.load("items/id,items/type,items/left,items/top,items/width,items/height");
    await context.sync();

    if (selectedShapes.items.length === 0) {
      return null;
    }

    for (const shape of selectedShapes.items) {
      if (shape.type === PowerPoint.ShapeType.placeholder) {
        shape.load("placeholderFormat/type,placeholderFormat/containedType");
      }
    }
    await context.sync();

    const sorted = [...selectedShapes.items].sort((a, b) => {
      if (a.top !== b.top) return a.top - b.top;
      return a.left - b.left;
    });

    return sorted.map((shape) => ({
      rect: shapeToRect(shape),
      placeholderId: isEmptyImagePlaceholder(shape) ? shape.id : null,
    }));
  });
}

async function findTargetBox(): Promise<TargetBox | null> {
  if (!Office.context.requirements.isSetSupported("PowerPointApi", "1.5")) {
    return null;
  }

  return PowerPoint.run(async (context) => {
    const selectedShapes = context.presentation.getSelectedShapes();
    selectedShapes.load("items/id,items/type,items/left,items/top,items/width,items/height");
    await context.sync();

    if (selectedShapes.items.length === 1) {
      const shape = selectedShapes.items[0];
      if (shape.type === PowerPoint.ShapeType.placeholder) {
        shape.load("placeholderFormat/type,placeholderFormat/containedType");
        await context.sync();
      }
      return {
        rect: shapeToRect(shape),
        placeholderId: isEmptyImagePlaceholder(shape) ? shape.id : null,
      };
    }

    const selectedSlides = context.presentation.getSelectedSlides();
    selectedSlides.load("items");
    await context.sync();

    if (selectedSlides.items.length === 0) {
      return null;
    }

    const slide = selectedSlides.getItemAt(0);
    const shapes = slide.shapes;
    shapes.load("items/id,items/type,items/left,items/top,items/width,items/height");
    await context.sync();

    const placeholderShapes: PowerPoint.Shape[] = [];
    for (const shape of shapes.items) {
      if (shape.type === PowerPoint.ShapeType.placeholder) {
        shape.load("placeholderFormat/type,placeholderFormat/containedType");
        placeholderShapes.push(shape);
      }
    }
    await context.sync();

    const emptyPlaceholders = placeholderShapes
      .filter(isEmptyImagePlaceholder)
      .sort((a, b) => {
        if (a.top !== b.top) return a.top - b.top;
        return a.left - b.left;
      });

    if (emptyPlaceholders.length === 0) {
      return null;
    }

    const target = emptyPlaceholders[0];
    return { rect: shapeToRect(target), placeholderId: target.id };
  });
}

// Deletes now-redundant empty placeholders AFTER their images are inserted, so a
// failed insert never leaves the slide mutated. Best-effort: a failure here is non-fatal.
async function deletePlaceholdersByIds(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  if (!Office.context.requirements.isSetSupported("PowerPointApi", "1.5")) return;

  await PowerPoint.run(async (context) => {
    const selectedSlides = context.presentation.getSelectedSlides();
    selectedSlides.load("items");
    await context.sync();

    if (selectedSlides.items.length === 0) return;

    const slide = selectedSlides.getItemAt(0);
    const shapes = slide.shapes;
    shapes.load("items/id");
    await context.sync();

    const idSet = new Set(ids);
    for (const shape of shapes.items) {
      if (idSet.has(shape.id)) {
        shape.delete();
      }
    }
    await context.sync();
  });
}

export async function insertImageIntoBox(
  base64: string,
  imgW: number,
  imgH: number,
  playId?: string,
  crop: PlayCrop = DEFAULT_CROP
): Promise<void> {
  assertOfficeReady();
  let target: TargetBox | null = null;
  try {
    target = await findTargetBox();
  } catch (err) {
    console.warn("[CHLK] Box targeting failed, falling back to legacy insert:", err);
  }

  if (target) {
    // Insert first; only remove the placeholder once the image is safely placed,
    // so a failed insert propagates without leaving a deleted placeholder behind.
    const rect = computeContainRect(target.rect, imgW, imgH);
    await insertAtCoordinates(base64, rect);
    if (target.placeholderId) {
      await deletePlaceholdersByIds([target.placeholderId]).catch((err) =>
        console.warn("[CHLK] Placeholder cleanup failed:", err)
      );
    }
    if (playId) {
      await tagShapeAtRect(playId, crop, rect);
    }
    return;
  }

  await insertViaOfficeLegacy(base64, 540);
  if (playId) {
    await tagSelectedShape(playId, crop);
  }
}

export async function insertImagesIntoBoxes(
  images: InsertImagePayload[]
): Promise<MultiInsertResult> {
  assertOfficeReady();
  const requested = images.length;

  if (requested === 0) {
    return { inserted: 0, requested: 0, boxes: 0, failed: 0 };
  }

  let targets: TargetBox[] | null;
  try {
    targets = await collectSelectedBoxes();
  } catch (err) {
    console.warn("[CHLK] Multi-box insert failed:", err);
    throw err;
  }

  if (!targets || targets.length === 0) {
    throw new Error("Select the boxes on the slide, then Insert.");
  }

  const count = Math.min(images.length, targets.length);
  const placeholderIdsToDelete: string[] = [];
  let inserted = 0;
  let failed = 0;

  for (let i = 0; i < count; i++) {
    const rect = computeContainRect(targets[i].rect, images[i].width, images[i].height);
    try {
      await insertAtCoordinates(images[i].png, rect);
      inserted++;
      const pid = targets[i].placeholderId;
      if (pid) placeholderIdsToDelete.push(pid);
      if (images[i].playId) {
        await tagShapeAtRect(images[i].playId!, DEFAULT_CROP, rect);
      }
    } catch (err) {
      console.error("[CHLK] Insert failed for image", i, err);
      failed++;
    }
  }

  // Only remove placeholders we actually filled; untouched placeholders stay intact.
  await deletePlaceholdersByIds(placeholderIdsToDelete).catch((err) =>
    console.warn("[CHLK] Placeholder cleanup failed:", err)
  );

  return { inserted, requested, boxes: targets.length, failed };
}
